// SceneLandmarks — maps planetSections to landmark meshes. For each
// scene with landmarkType !== 'none' && !== 'path', render the
// corresponding landmark at scene.landmarkPos. All landmarks orient
// to the planet's up vector at their position.
//
// Hover: small scale-up + color highlight. Selected (when active
// scene is this one): larger glow + label appears. Click: sets
// openPanelId to the scene's panel key via the supplied callback.

import { useMemo, useRef } from 'react';
import * as THREE from 'three';
import type { Group } from 'three';

import { SCENES, type SceneConfig, type LandmarkType } from './planetSections';
import { openDetail } from './SatelliteInteraction';
import { House } from './landmarks/House';
import { Workshop } from './landmarks/Workshop';
import { SkillStones } from './landmarks/SkillStones';
import { BookFragments } from './landmarks/BookFragments';
import { SignalTower } from './landmarks/SignalTower';
import { VISTA_SCENE_IDS } from './SurfaceVistas';

const PLANET_RADIUS = 1.5;

// Landmarks past this camera distance drop their decorative elements
// (chimney, window glow, antenna, point light) and switch to a single
// silhouette mesh. Tied to the spec's "if camera distance to them is
// > 6, swap to a single mesh version" rule.
const LANDMARK_LOD_DISTANCE = 6;

export interface SceneLandmarksProps {
  /** Active scene index (0..6). */
  activeSceneIndex: number;
  /** Per-landmark hover state — set by the interactive group. */
  hoveredLandmarkKey?: string | null;
  /** Called when a landmark is clicked. */
  onLandmarkClick?: (scene: SceneConfig) => void;
}

/** Choose the right landmark component for a landmark type. */
function renderLandmark(
  scene: SceneConfig,
  isActive: boolean,
  isHovered: boolean,
  groupRef: React.MutableRefObject<Group | null>,
) {
  // The landmark is placed at scene.landmarkPos (already in world
  // space relative to the planet). The orientation aligns the
  // landmark's +Y with the planet's "up" at that point — i.e. the
  // outward normal of the planet surface.
  const [x, y, z] = scene.landmarkPos;
  const pos: [number, number, number] = [x, y, z];
  const r = Math.hypot(x, y, z) || 1;
  // Default "up" = world Y; rotate so the landmark's local up points
  // outward from the planet center.
  const outwardY = new THREE.Vector3(x, y, z).normalize();
  const quat = new THREE.Quaternion();
  quat.setFromUnitVectors(new THREE.Vector3(0, 1, 0), outwardY);
  const euler = new THREE.Euler().setFromQuaternion(quat);
  const rot: [number, number, number] = [euler.x, euler.y, euler.z];

  // LOD: if the scene's framed camera distance to the landmark is
  // greater than the simplify threshold, render the silhouette-only
  // (single-mesh) version. The scene.cameraPos comes from
  // planetSections and is what the narrative-mode camera rig
  // actually settles at, so this is a close-enough proxy for real
  // distance.
  const [cx, cy, cz] = scene.cameraPos;
  const cameraDistance = Math.hypot(cx - x, cy - y, cz - z);
  const isFar = cameraDistance > LANDMARK_LOD_DISTANCE;

  switch (scene.landmarkType as LandmarkType) {
    case 'house':
      return (
        <House
          key={scene.id}
          position={pos}
          rotation={rot}
          isActive={isActive}
          isHovered={isHovered}
          isFar={isFar}
          groupRef={groupRef}
        />
      );
    case 'workshop':
      return (
        <Workshop
          key={scene.id}
          position={pos}
          rotation={rot}
          isActive={isActive}
          isHovered={isHovered}
          isFar={isFar}
          groupRef={groupRef}
        />
      );
    case 'skill_stones':
      return (
        <SkillStones
          key={scene.id}
          position={pos}
          rotation={rot}
          isActive={isActive}
          isHovered={isHovered}
          groupRef={groupRef}
        />
      );
    case 'book_fragments':
      return (
        <BookFragments
          key={scene.id}
          position={pos}
          rotation={rot}
          isActive={isActive}
          isHovered={isHovered}
          groupRef={groupRef}
        />
      );
    case 'signal_tower':
      return (
        <SignalTower
          key={scene.id}
          position={pos}
          rotation={rot}
          isActive={isActive}
          isHovered={isHovered}
          groupRef={groupRef}
        />
      );
    default:
      return null;
  }
}

export function SceneLandmarks({
  activeSceneIndex,
  hoveredLandmarkKey,
  onLandmarkClick,
}: SceneLandmarksProps) {
  // One ref per scene that has a landmark.
  const refs = useRef<Record<number, React.MutableRefObject<Group | null>>>({});

  // Prefer a SurfaceVista when its chapter is active; otherwise keep the
  // simpler landmark so the planet still has quiet markers from afar.
  const scenesWithLandmarks = useMemo(
    () =>
      SCENES.filter((s) => {
        if (s.landmarkType === 'none' || s.landmarkType === 'path') return false;
        if (VISTA_SCENE_IDS.has(s.id) && s.id === activeSceneIndex) return false;
        return true;
      }),
    [activeSceneIndex],
  );

  // Attach pointer handlers to each landmark group via the ref.
  const setRef = (id: number) => {
    if (!refs.current[id]) {
      refs.current[id] = { current: null };
    }
    return refs.current[id];
  };

  return (
    <group name="scene-landmarks">
      {scenesWithLandmarks.map((scene) => {
        const isActive = scene.id === activeSceneIndex;
        const isHovered = hoveredLandmarkKey === scene.key;
        const groupRef = setRef(scene.id);
        return (
          <group
            key={scene.id}
            ref={(node) => {
              groupRef.current = node;
            }}
            onPointerOver={(e) => {
              e.stopPropagation();
              if (typeof window !== 'undefined') {
                window.__planetHoveredLandmark = scene.key;
              }
              document.body.style.cursor = 'pointer';
            }}
            onPointerOut={(e) => {
              e.stopPropagation();
              if (typeof window !== 'undefined') {
                window.__planetHoveredLandmark = null;
              }
              document.body.style.cursor = '';
            }}
            onClick={(e) => {
              e.stopPropagation();
              // Exploration mode: fly to the landmark, show info card.
              if (
                typeof window !== 'undefined' &&
                window.__planetMode === 'exploration'
              ) {
                if (window.__planetShowInfoCard && groupRef.current) {
                  const wp = new THREE.Vector3();
                  groupRef.current.getWorldPosition(wp);
                  window.__planetShowInfoCard({
                    kind: 'landmark',
                    id: scene.landmarkType as string,
                    title: scene.name,
                    blurb: scene.subtitle,
                    worldPos: [wp.x, wp.y, wp.z],
                  });
                }
                return;
              }
              // Map the scene key to a deep-dive detail type. Each
              // landmark corresponds to one of the chapter panels, so
              // clicking it opens the matching detail view.
              const detailTypeForKey: Record<string, 'about' | 'skills' | 'milestone' | 'contact' | 'project' | 'note'> = {
                about: 'about',
                skills: 'skills',
                projects: 'project',
                notes: 'note',
                timeline: 'milestone',
                contact: 'contact',
              };
              const type = detailTypeForKey[scene.key] ?? 'about';
              // For 'project' / 'note' / 'milestone' we need an id. The
              // project/note landmark opens a list of satellites/notes,
              // so we leave the detail panel closed for now and let
              // the consumer pick. For 'milestone' we open the first
              // year (the consumer can navigate further from there).
              let id = scene.key;
              if (type === 'milestone') {
                // Open the first timeline milestone. The user can
                // navigate through others from the timeline panel.
                id = '2019';
              } else if (type === 'project') {
                // Clicking the workshop landmark opens the project list.
                // We don't open the detail panel; the right-rail
                // SatelliteInfoCard handles the project list.
                openDetail(null);
                onLandmarkClick?.(scene);
                return;
              } else if (type === 'note') {
                openDetail(null);
                onLandmarkClick?.(scene);
                return;
              }
              openDetail({ type, id });
            }}
            userData={{ landmarkType: scene.landmarkType, sceneId: scene.id }}
          >
            {renderLandmark(scene, isActive, isHovered, groupRef)}
          </group>
        );
      })}
    </group>
  );
}

export default SceneLandmarks;

// Augment the global Window type to include the hover ref.
declare global {
  interface Window {
    __planetHoveredLandmark?: string | null;
  }
}
